import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  HStack,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { ArticleProfile } from '@grcs/types/DataTypes';
import { normalizeArticleSpacing } from '@grcs/utils/content/normalizeArticleSpacing';
interface Props {
  content: ArticleProfile;
  reading: boolean;
  completed?: boolean;
  storageKey: string;
  showPinyin: boolean;
  onTextDoubleClick: React.MouseEventHandler<HTMLDivElement>;
  onUnlock: (unlocked: boolean) => void;
  onRestart: () => void;
  children: React.ReactNode;
}

export function ReadingWorkspace({
  content,
  reading,
  completed = false,
  storageKey,
  showPinyin,
  onTextDoubleClick,
  onUnlock,
  onRestart,
  children,
}: Props) {
  const articleHtml = useMemo(
    () =>
      normalizeArticleSpacing(
        showPinyin
          ? content.article_pinyin || content.article
          : content.article,
      ),
    [showPinyin, content.article_pinyin, content.article],
  );
  const revision = JSON.stringify([content.article, content.question]);
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === revision;
    } catch {
      return false;
    }
  });
  const [stage, setStage] = useState<'article' | 'questions'>(
    reading && unlocked ? 'questions' : 'article',
  );
  const articleRef = useRef<HTMLDivElement>(null);
  const questionRef = useRef<HTMLDivElement>(null);
  const paper = useColorModeValue('white', 'gray.800');
  const muted = useColorModeValue('gray.600', 'gray.300');
  const accent = useColorModeValue('blue.50', 'gray.700');

  useEffect(() => {
    // Leaving the page ends this attempt; a later visit must start with reading.
    return () => {
      if (reading) {
        try {
          localStorage.removeItem(storageKey);
        } catch {
          /* Storage is optional. */
        }
      }
    };
  }, [reading, storageKey]);

  useEffect(() => {
    onUnlock(!reading || unlocked);
  }, [reading, unlocked, onUnlock]);
  useEffect(() => {
    const target =
      stage === 'questions' ? questionRef.current : articleRef.current;
    target?.focus();
    target?.scrollIntoView?.({ block: 'start' });
  }, [stage]);

  const navigateToStage = (next: 'questions') => {
    setStage(next);
    const target = questionRef.current;
    target?.focus();
    target?.scrollIntoView?.({ block: 'start' });
  };

  const openQuestions = () => {
    setUnlocked(true);
    try {
      localStorage.setItem(storageKey, revision);
    } catch {
      /* Reading still works without storage. */
    }
    navigateToStage('questions');
  };
  const returnToArticle = () => {
    articleRef.current?.focus();
    articleRef.current?.scrollIntoView?.({ block: 'start' });
  };

  const restartReading = () => {
    if (!reading) {
      returnToArticle();
      return;
    }
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* Storage is optional. */
    }
    setUnlocked(false);
    onUnlock(false);
    onRestart();
    setStage('article');
  };

  return (
    <Stack spacing={6}>
      {(!reading || stage !== 'questions') && (
        <Box
          as="article"
          id="article"
          ref={articleRef}
          tabIndex={-1}
          aria-label="阅读文章"
          // Marks eligible speech text. Native selection stays enabled for
          // touch long-press; desktop activation comes from double-click only.
          data-pronunciation-text
          userSelect="text"
          onDoubleClick={onTextDoubleClick}
          bg={paper}
          borderRadius="2xl"
          borderWidth="1px"
          p={{ base: 4, md: 8 }}
          scrollMarginTop="24px"
        >
          <Heading size="md" textAlign="center" lineHeight="1.5" mb={5}>
            {content.title}
          </Heading>
          <Box
            lang="zh"
            fontSize="md"
            lineHeight="1.8"
            whiteSpace="pre-wrap"
            maxW="65ch"
            mx="auto"
            sx={{
              '& p': { margin: '0 0 0.5em !important', textIndent: '2em' },
              '& p:last-child': { marginBottom: '0 !important' },
              '& li': { marginBottom: '0.25em' },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                margin: '24px auto',
                borderRadius: '12px',
              },
              '& ruby': { rubyAlign: 'center' },
              '& rt': { fontSize: '0.55em' },
            }}
            dangerouslySetInnerHTML={{
              __html: articleHtml,
            }}
          />
          {reading && (
            <Stack
              align="center"
              mt={10}
              pt={6}
              borderTopWidth="1px"
              sx={{ '@media print': { display: 'none' } }}
            >
              <Button colorScheme="green" onClick={openQuestions}>
                阅读完成，前往答题
              </Button>
            </Stack>
          )}
        </Box>
      )}

      {(!reading || (unlocked && stage === 'questions')) && (
        <Box
          id="question"
          ref={questionRef}
          tabIndex={-1}
          aria-label="回答问题"
          bg={paper}
          borderWidth="1px"
          borderRadius="2xl"
          p={{ base: 4, md: 8 }}
          scrollMarginTop="24px"
          sx={{ '@media print': { pageBreakBefore: 'always' } }}
        >
          <HStack
            justify="space-between"
            flexWrap="wrap"
            mb={2}
            display={completed ? 'none' : undefined}
          >
            <Button
              variant="outline"
              size="sm"
              onClick={restartReading}
              colorScheme="orange"
              sx={{ '@media print': { display: 'none' } }}
            >
              重读文章 ↑
            </Button>
          </HStack>
          {/* Question text uses the same native selection and speech flow. */}
          <Box
            data-pronunciation-text
            userSelect="text"
            onDoubleClick={onTextDoubleClick}
          >
            {children}
          </Box>
        </Box>
      )}
    </Stack>
  );
}
